import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { z, type ZodObjectDef, ZodType } from 'zod';

export type QueryParamsMethods<T extends {}> = {
  push: (paramsDescriptor: Descriptor<T>) => void;
  replace: (paramsDescriptor: Descriptor<T>) => void;
};

export function useQueryParams<
  Z extends ZodType<any, ZodObjectDef, any>,
  T extends {} = z.infer<Z>,
>(schema: Z): [T, QueryParamsMethods<T>] {
  const location = useLocation();
  const history = useHistory();

  const params = useMemo<T>(
    () => paramsFromSearchParams(schema, new URLSearchParams(location.search)),
    [schema, location],
  );

  const methods = useMemo<QueryParamsMethods<T>>(
    () => ({
      push(descriptor) {
        const currentSearch = new URLSearchParams(history.location.search);
        const prevParams = paramsFromSearchParams(schema, currentSearch);
        const newParams = applyDescriptor(descriptor, prevParams);
        const searchParams = applyNewSearchParams(currentSearch, newParams);

        history.push({
          ...history.location,
          search: searchParams.toString(),
        });
      },
      replace(descriptor) {
        const currentSearch = new URLSearchParams(history.location.search);
        const prevParams = paramsFromSearchParams(schema, currentSearch);
        const newParams = applyDescriptor(descriptor, prevParams);
        const searchParams = applyNewSearchParams(currentSearch, newParams);

        history.replace({
          ...history.location,
          search: searchParams.toString(),
        });
      },
    }),
    [history, schema],
  );

  return [params, methods];
}

/**
 * Parse query params from a location
 */
function paramsFromSearchParams<
  Z extends ZodType<any, ZodObjectDef, any>,
  T extends z.infer<Z>,
>(schema: Z, searchParams: URLSearchParams): T {
  const queryParams = Object.fromEntries(searchParams.entries());
  return schema.parse(queryParams);
}

function applyNewSearchParams<O extends {}>(
  prevSearchParams: URLSearchParams,
  obj: O,
): URLSearchParams {
  const searchParams = new URLSearchParams(prevSearchParams);

  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null) {
      searchParams.delete(key);
      continue;
    }

    searchParams.set(key, String(value));
  }

  return searchParams;
}

/**
 * Apply a descriptor to a value
 * a descriptor can be a value or a function that takes the previous value and returns a new value
 */
type Descriptor<T extends {}> = Partial<T> | ((previous: T) => Partial<T>);

/**
 * Apply a descriptor to a value
 */
function applyDescriptor<T extends {}>(
  descriptor: Descriptor<T>,
  previous: T,
): Partial<T> {
  if (typeof descriptor === 'function') {
    return descriptor(previous);
  }

  return descriptor;
}
